/**
 * The alphabet drill's pure logic: which letters are drillable, how a card is built, how a
 * wrong answer is weighted back to the front, and where the distractors come from.
 *
 * It is kept out of the Svelte component for the same reason the keymap and the coordinate
 * maths are: it is the part with edge cases, and it should be testable without a DOM. The
 * component owns the reactive state and the localStorage; this module is a set of functions
 * over the generated `ALPHABET` table.
 *
 * Two honesty constraints are enforced here rather than left to the UI:
 *
 * - **Only letters with a reference form are drillable.** A card for a letter with no glyph
 *   would have to invent one, so the filter is on `glyph !== null` rather than on a letter
 *   list. All 28 attested letters currently have a form — `ḏ`, `ġ`, `k` and `ẓ` were missing
 *   until their forms were recovered from the web archive (see `glyph_archive`) — but the
 *   filter stays, because the deck size must follow the data rather than a constant. The panel
 *   reads its counts from `ALPHABET_SOURCE` for the same reason. `illegible` and `unknown` are
 *   not letters and are excluded too.
 * - **Distractors are the *visually* nearest glyphs**, from the generated `similar` list — a
 *   rendering-space similarity, not a claim about which letters confuse a reader on stone.
 */

import { ALPHABET, type AlphabetEntry } from './alphabet.generated';

export type { AlphabetEntry };

/** A source of randomness, injectable so the deck logic is deterministic under test. */
export type Rng = () => number;

/** How many choices a multiple-choice card offers, the answer included. */
export const OPTION_COUNT = 4;

/**
 * The Leitner box count. Bucket 0 is "just missed or never seen"; the top bucket is
 * "consistently right". A letter climbs one box per correct answer and falls straight back to
 * 0 on a miss — the sharp reset is the point of a Leitner system, so a letter you thought you
 * knew and then failed comes back soon rather than in twenty cards' time.
 */
export const LEITNER_BUCKETS = 5;

/** One letter's running record. Serialized to localStorage by the progress store. */
export interface LetterStat {
  bucket: number;
  right: number;
  wrong: number;
  /** ISO timestamp of the last time this letter was shown. */
  lastSeen: string;
}

export type DrillDirection = 'glyphToLetter' | 'letterToGlyph';

export interface DrillCard {
  direction: DrillDirection;
  /** The letter under test. */
  prompt: AlphabetEntry;
  /** The choices, answer included, already shuffled. */
  options: AlphabetEntry[];
  answerId: number;
}

/**
 * The drillable deck: attested letters that actually have a reference form. Frozen at module
 * load from the generated table, so both directions and the distractor pool agree on it.
 */
export const DRILLABLE: readonly AlphabetEntry[] = ALPHABET.filter(
  (entry) => entry.kind === 'letter' && entry.glyph !== null,
);

/** Fisher-Yates, in place, driven by the injectable RNG. Returns the same array. */
export function shuffle<T>(items: T[], rng: Rng = Math.random): T[] {
  for (let i = items.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [items[i], items[j]] = [items[j]!, items[i]!];
  }
  return items;
}

/**
 * The options for one prompt: the answer plus up to `count - 1` distractors, visually-near
 * ones first (from `prompt.similar`, filtered to the drillable pool), padded with other pool
 * letters when the similar list is short, and finally shuffled so the answer's position is not
 * a tell.
 */
export function buildOptions(
  prompt: AlphabetEntry,
  pool: readonly AlphabetEntry[] = DRILLABLE,
  rng: Rng = Math.random,
  count: number = OPTION_COUNT,
): AlphabetEntry[] {
  const byId = new Map(pool.map((entry) => [entry.id, entry]));
  const distractors: AlphabetEntry[] = [];

  for (const id of prompt.similar) {
    if (id === prompt.id) continue;
    const entry = byId.get(id);
    if (entry && !distractors.includes(entry)) distractors.push(entry);
    if (distractors.length >= count - 1) break;
  }

  if (distractors.length < count - 1) {
    const rest = pool.filter((entry) => entry.id !== prompt.id && !distractors.includes(entry));
    shuffle(rest, rng);
    for (const entry of rest) {
      distractors.push(entry);
      if (distractors.length >= count - 1) break;
    }
  }

  return shuffle([prompt, ...distractors], rng);
}

/** Build a full card for a prompt and direction. */
export function makeCard(
  prompt: AlphabetEntry,
  direction: DrillDirection,
  pool: readonly AlphabetEntry[] = DRILLABLE,
  rng: Rng = Math.random,
): DrillCard {
  return { direction, prompt, options: buildOptions(prompt, pool, rng), answerId: prompt.id };
}

/**
 * The Leitner weight of a letter: weaker letters are heavier, so they resurface first. Bucket
 * 0 weighs `LEITNER_BUCKETS`, the top bucket weighs 1 — never 0, so even a mastered letter is
 * still occasionally revisited rather than dropped forever.
 */
export function weightFor(stat: LetterStat | undefined): number {
  const bucket = Math.min(Math.max(stat?.bucket ?? 0, 0), LEITNER_BUCKETS - 1);
  return LEITNER_BUCKETS - bucket;
}

/**
 * Pick the next letter to test, weighted toward the weakest, avoiding an immediate repeat.
 *
 * `statFor` is a lookup rather than a map so the caller can pass a reactive store or a plain
 * object without this module knowing which.
 */
export function pickNext(
  pool: readonly AlphabetEntry[],
  statFor: (id: number) => LetterStat | undefined,
  rng: Rng = Math.random,
  exclude?: number,
): AlphabetEntry | null {
  const candidates =
    exclude != null && pool.length > 1
      ? pool.filter((entry) => entry.id !== exclude)
      : [...pool];
  if (candidates.length === 0) return null;

  const weights = candidates.map((entry) => weightFor(statFor(entry.id)));
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  let threshold = rng() * total;
  for (let i = 0; i < candidates.length; i += 1) {
    threshold -= weights[i]!;
    if (threshold < 0) return candidates[i]!;
  }
  return candidates[candidates.length - 1]!;
}

/**
 * Fold one graded answer into a letter's stat. Correct climbs one box (capped); wrong resets
 * to box 0. Pure: returns a new stat, never mutates.
 */
export function grade(
  stat: LetterStat | undefined,
  correct: boolean,
  now: string = new Date().toISOString(),
): LetterStat {
  const base = stat ?? { bucket: 0, right: 0, wrong: 0, lastSeen: now };
  return {
    bucket: correct ? Math.min(base.bucket + 1, LEITNER_BUCKETS - 1) : 0,
    right: base.right + (correct ? 1 : 0),
    wrong: base.wrong + (correct ? 0 : 1),
    lastSeen: now,
  };
}

/** How many of the drillable letters have reached the top Leitner box. */
export function masteredCount(
  statFor: (id: number) => LetterStat | undefined,
  pool: readonly AlphabetEntry[] = DRILLABLE,
): number {
  return pool.filter((entry) => (statFor(entry.id)?.bucket ?? 0) >= LEITNER_BUCKETS - 1).length;
}
